import { useFormContext } from 'react-hook-form'
import { BefehlFormAuftrag, BefehlFormField } from '../Generic'
import { useTranslation } from 'react-i18next'
import { BetriebsstelleField, BetriebsstellenartField, Input } from '../../form'
import BorderedBox from '../Generic/BorderedBox'
import { useMemo } from 'react'
import ZugmeldestelleWrapper from '../Generic/ZugmeldestelleWrapper'

export interface Auftrag34_10FormProps {
  befehlKennung: string
  fieldPrefix: string
  readOnly: boolean
  'data-testid': string
  activateEmptyFieldWarnings: boolean
}

export default function Auftrag34_10Form({
  fieldPrefix,
  readOnly,
  'data-testid': testId,
  activateEmptyFieldWarnings = false
}: Readonly<Auftrag34_10FormProps>) {
  const formContext = useFormContext()
  const { control, formState, watch } = formContext
  const { errors } = formState
  const { t } = useTranslation('ui-befehle')

  const fieldPaths = useMemo(
    () => ({
      betriebsstellenart3412: `${fieldPrefix}.field_34_12_von.art`,
      betriebsstellenart3413: `${fieldPrefix}.field_34_13_bis.art`
    }),
    [fieldPrefix]
  )

  const [betriebsstellenart3412, betriebsstellenart3413] = watch([
    fieldPaths.betriebsstellenart3412,
    fieldPaths.betriebsstellenart3413
  ])

  return (
    <BefehlFormAuftrag
      auftragKennung="34.10"
      fieldName={`${fieldPrefix}.enabled`}
      readOnly={true}
      disabled={false}
      testId={testId}
    >
      <div className="flex flex-col sm:flex-row w-full items-baseline gap-1 pl-0.5">
        <span data-testid={`${testId}-text`}>{t('auftraege.34_10.label')}</span>{' '}
        <div>
          <BefehlFormField
            name={`${fieldPrefix}.field_34_11_kmh`}
            control={control}
            label={t('auftraege.34_10.kmh.label')}
            readOnly={readOnly}
            activateEmptyWarning={activateEmptyFieldWarnings}
            data-testid={`${testId}-feld-34-11`}
            className="min-w-15"
            render={({ field: field_34_11 }) => (
              <Input
                {...field_34_11}
                label={t('auftraege.34_10.kmh.label')}
                errors={errors}
                data-testid={`${testId}-feld-34-11-inner`}
              />
            )}
          ></BefehlFormField>
          <span data-testid={`${testId}-feld-34-11-label`}>
            {t('auftraege.34_10.kmh.input_label')}
          </span>
        </div>
        <span data-testid={`${testId}-text-von`}>
          {t('auftraege.34_10.von')}
        </span>
      </div>

      <BorderedBox
        data-testid={`${testId}-feld-34-12-bordered-box`}
        footer={
          <span data-testid={`${testId}-feld-34-12-label`}>
            {t('auftraege.34_10.zugmeldestelle.34_12_label')}
          </span>
        }
      >
        <ZugmeldestelleWrapper
          betriebsstellenartField={
            <BefehlFormField
              name={fieldPaths.betriebsstellenart3412}
              control={control}
              label={t(
                'auftraege.34_10.zugmeldestelle.betriebsstellenart_label'
              )}
              readOnly={readOnly}
              activateEmptyWarning={activateEmptyFieldWarnings}
              data-testid={`${testId}-feld-34-12-art`}
              render={({ field: field_34_12_art }) => (
                <BetriebsstellenartField
                  {...field_34_12_art}
                  label={t(
                    'auftraege.34_10.zugmeldestelle.betriebsstellenart_label'
                  )}
                  variant="solid"
                  className="w-full"
                  errors={errors}
                  data-testid={`${testId}-feld-34-12-art-inner`}
                  selectClassName="w-full"
                />
              )}
            />
          }
          bestriebsstelleField={
            <BefehlFormField
              name={`${fieldPrefix}.field_34_12_von.name`}
              control={control}
              label={t('auftraege.34_10.zugmeldestelle.zugmeldestelle_label')}
              readOnly={readOnly}
              activateEmptyWarning={activateEmptyFieldWarnings}
              data-testid={`${testId}-feld-34-12-name`}
              render={({ field: field_34_12_name }) => (
                <BetriebsstelleField
                  {...field_34_12_name}
                  label={t(
                    'auftraege.34_10.zugmeldestelle.zugmeldestelle_label'
                  )}
                  variant="solid"
                  className="w-full"
                  errors={errors}
                  data-testid={`${testId}-feld-34-12-name-inner`}
                  typ={betriebsstellenart3412}
                />
              )}
            />
          }
        />
      </BorderedBox>

      <span className="pl-0.5" data-testid={`${testId}-text-bis`}>
        {t('auftraege.34_10.bis')}
      </span>

      <BorderedBox
        data-testid={`${testId}-feld-34-13-bordered-box`}
        footer={
          <span data-testid={`${testId}-feld-34-13-label`}>
            {t('auftraege.34_10.zugmeldestelle.34_13_label')}
          </span>
        }
      >
        <ZugmeldestelleWrapper
          betriebsstellenartField={
            <BefehlFormField
              name={fieldPaths.betriebsstellenart3413}
              control={control}
              label={t(
                'auftraege.34_10.zugmeldestelle.betriebsstellenart_label'
              )}
              readOnly={readOnly}
              activateEmptyWarning={activateEmptyFieldWarnings}
              data-testid={`${testId}-feld-34-13-art`}
              render={({ field: field_34_13_art }) => (
                <BetriebsstellenartField
                  {...field_34_13_art}
                  label={t(
                    'auftraege.34_10.zugmeldestelle.betriebsstellenart_label'
                  )}
                  variant="solid"
                  className="w-full"
                  errors={errors}
                  data-testid={`${testId}-feld-34-13-art-inner`}
                  selectClassName="w-full"
                />
              )}
            />
          }
          bestriebsstelleField={
            <BefehlFormField
              name={`${fieldPrefix}.field_34_13_bis.name`}
              control={control}
              label={t('auftraege.34_10.zugmeldestelle.zugmeldestelle_label')}
              readOnly={readOnly}
              activateEmptyWarning={activateEmptyFieldWarnings}
              data-testid={`${testId}-feld-34-13-name`}
              render={({ field: field_34_13_name }) => (
                <BetriebsstelleField
                  {...field_34_13_name}
                  label={t(
                    'auftraege.34_10.zugmeldestelle.zugmeldestelle_label'
                  )}
                  variant="solid"
                  className="w-full"
                  errors={errors}
                  data-testid={`${testId}-feld-34-13-name-inner`}
                  typ={betriebsstellenart3413}
                />
              )}
            />
          }
        />
      </BorderedBox>
      <span className={'flex pl-0.5'} data-testid={`${testId}-text-einstellen`}>
        {t('auftraege.34_10.einstellen')}
      </span>
    </BefehlFormAuftrag>
  )
}
